import React, { useState, useEffect, useRef, type MouseEvent as ReactMouseEvent } from 'react';
import { Link, useLocation, useNavigate, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart, faHeart, faUser, faSignOutAlt, faBars, faTimes, faChevronDown, faClipboardList } from "@fortawesome/free-solid-svg-icons";
import Registration from "../../pages/auth/Registration";
import Login from "../../pages/auth/Login";
import BrandLogo from "../BrandLogo";
import API_ENDPOINTS from "../../api/apiConfig";
import { useUser } from "../../context/UserContext";
import type { CartItem, CategoryWithSubcategories } from "../../types";
import megaFeature from '../../assets/images/banner-image-2.jpg';
import '../../assets/css/theme.css';
import '../../assets/css/navbar.css';

/** Keep in sync with --ff-nav-h in theme.css */
const NAV_HEIGHT = '72px';

const getAccessToken = (): string | null => localStorage.getItem('authToken');

// Run immediately when module loads — before first render
// This prevents the white flash caused by useEffect running after paint
(function applyBodyOffset() {
  const isHome = window.location.pathname === '/';
  if (isHome) {
    document.documentElement.classList.add('home-page');
    document.body.style.setProperty('padding-top', '0px', 'important');
    document.body.style.setProperty('margin-top', '0px', 'important');
  } else {
    document.documentElement.classList.remove('home-page');
    document.body.style.setProperty('padding-top', NAV_HEIGHT, 'important');
    document.body.style.setProperty('margin-top', '0px', 'important');
  }
})();

const Navbar = () => {
  const [showRegistration, setShowRegistration] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('authToken'));
  const [categoryData, setCategoryData] = useState<CategoryWithSubcategories[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const { user } = useUser();
  const [scrolled, setScrolled] = useState(false);
  // After clicking a mega-menu link the pointer is still over the panel, so CSS :hover keeps it open.
  // We hide it explicitly until the pointer leaves the item.
  const [megaHidden, setMegaHidden] = useState(false);

  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  /* ── Homepage: remove body top offset so hero sits behind transparent navbar ── */
  useEffect(() => {
    if (isHomePage) {
      document.body.style.setProperty('padding-top', '0px', 'important');
      document.body.style.setProperty('margin-top', '0px', 'important');
      document.body.classList.add('home-page');
    } else {
      document.body.style.setProperty('padding-top', NAV_HEIGHT, 'important');
      document.body.style.setProperty('margin-top', '0px', 'important');
      document.body.classList.remove('home-page');
    }
    return () => {
      document.body.style.removeProperty('padding-top');
      document.body.style.removeProperty('margin-top');
      document.body.classList.remove('home-page');
    };
  }, [isHomePage]);

  /* ── Scroll ── */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ── Fetch categories ── */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = getAccessToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(API_ENDPOINTS.GET_CATEGORY_SUBCATEGORY, { method: 'POST', headers });
        const data = await response.json();
        setCategoryData(response.ok && Array.isArray(data.data) ? data.data : []);
      } catch { setCategoryData([]); }
    };
    fetchCategories();
  }, []);

  /* ── Cart count ── */
  const updateCartCount = () => {
    try {
      const cart: CartItem[] = JSON.parse(localStorage.getItem('cart') || 'null') || [];
      setCartCount(cart.reduce((sum, item) => sum + (item.quantity || 1), 0));
    } catch { setCartCount(0); }
  };
  useEffect(() => {
    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    return () => window.removeEventListener('cartUpdated', updateCartCount);
  }, []);

  /* ── Reset on route change ── */
  useEffect(() => {
    setShowRegistration(false);
    setShowLogin(false);
    setIsLoggedIn(!!localStorage.getItem('authToken'));
    setIsMenuOpen(false);
    setIsCategoryOpen(false);
    setIsAccountDropdownOpen(false);
  }, [location]);

  /* ── Mobile body lock ── */
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  /* ── Outside click for account dropdown ── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node))
        setIsAccountDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeMenu = () => { setIsMenuOpen(false); setIsCategoryOpen(false); };

  const handleMegaNavigate = (e: ReactMouseEvent<HTMLElement>) => {
    closeMenu();
    setMegaHidden(true);
    e.currentTarget.blur(); // drop :focus-within so the panel really closes
  };

  const handleProfileClick      = () => { setIsAccountDropdownOpen(false); closeMenu(); navigate('/profile'); };
  const handleWishlistClick     = () => { setIsAccountDropdownOpen(false); closeMenu(); navigate('/wishlist'); };
  const handleOrderHistoryClick = () => { setIsAccountDropdownOpen(false); closeMenu(); navigate('/order-history'); };

  const handleLogout = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    localStorage.removeItem('authToken');
    localStorage.removeItem('cart');
    localStorage.removeItem("pu_name");
    window.dispatchEvent(new Event('cartUpdated'));
    setIsLoggedIn(false);
    setIsAccountDropdownOpen(false);
    closeMenu();
    navigate('/');
    window.location.reload();
  };

  const onDark = isHomePage && !scrolled;

  const navClasses = [
    'ffn-nav',
    onDark ? 'ffn-nav--clear' : 'ffn-nav--solid',
    scrolled ? 'ffn-nav--scrolled' : '',
  ].filter(Boolean).join(' ');

  const displayName = isLoggedIn && user?.pu_name
    ? user.pu_name.split(' ')[0]
    : 'Account';
  const initial = displayName.charAt(0).toUpperCase();

  const navLinkClass = ({ isActive }: { isActive: boolean }) => `ffn-link${isActive ? ' is-active' : ''}`;
  const mobileLinkClass = ({ isActive }: { isActive: boolean }) => `ffn-m-link${isActive ? ' is-active' : ''}`;

  return (
    <div className="ffn-wrap">
      <nav id="app-navbar" className={navClasses} role="navigation">
        <div className="ffn-inner">

          {/* Logo */}
          <Link to="/" className="ffn-brand" aria-label="Fashion Frame – home">
            <BrandLogo tone={onDark ? 'light' : 'dark'} />
          </Link>

          {/* Desktop links */}
          <ul className="ffn-links">
            <li className="ffn-item">
              <NavLink to="/" end className={navLinkClass}>Home</NavLink>
            </li>

            <li
              className={`ffn-item ffn-item--mega${megaHidden ? ' is-hidden' : ''}`}
              onMouseLeave={() => setMegaHidden(false)}
            >
              <button type="button" className="ffn-link ffn-link--btn" aria-haspopup="true">
                Collection
                <FontAwesomeIcon icon={faChevronDown} className="ffn-caret" />
              </button>

              {categoryData.length > 0 && (
                <div className="ffn-mega">
                  <div className="ffn-mega-inner">
                    <div className="ffn-mega-cols">
                      {categoryData.map((category) => (
                        <div className="ffn-mega-col" key={category.category_id}>
                          <Link to={`/collection/${category.c_slug}`} className="ffn-mega-head" onClick={handleMegaNavigate}>
                            {category.c_name}
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                              <path d="M2 7H12M8 3L12 7L8 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Link>
                          <ul className="ffn-mega-list">
                            {category.subcategories.map((sub) => (
                              <li key={sub.sc_slug}>
                                <Link to={`/products/${sub.sc_slug}`} title={sub.sc_name} onClick={handleMegaNavigate}>
                                  {sub.sc_name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    <Link to="/products" className="ffn-mega-feature" onClick={handleMegaNavigate}>
                      <img src={megaFeature} alt="" />
                      <span className="ffn-mega-feature-shade" />
                      <span className="ffn-mega-feature-copy">
                        <small>New season</small>
                        <strong>Shop all <em>products</em></strong>
                        <span className="ffn-mega-feature-cta">Explore →</span>
                      </span>
                    </Link>
                  </div>
                </div>
              )}
            </li>

            <li className="ffn-item"><NavLink to="/products" className={navLinkClass}>Products</NavLink></li>
            <li className="ffn-item"><NavLink to="/pages/about-us" className={navLinkClass}>About Us</NavLink></li>
            <li className="ffn-item"><NavLink to="/contact" className={navLinkClass}>Contact</NavLink></li>
          </ul>

          {/* Desktop actions */}
          <div className="ffn-actions">
            <Link to="/cart" className="ffn-icon-btn" aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}>
              <FontAwesomeIcon icon={faShoppingCart} />
              {cartCount > 0 && <span className="ffn-badge">{cartCount}</span>}
            </Link>

            <div className="ffn-account" ref={accountDropdownRef}>
              <button
                type="button"
                className={`ffn-account-btn${isAccountDropdownOpen ? ' is-open' : ''}`}
                aria-haspopup="true"
                aria-expanded={isAccountDropdownOpen}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsAccountDropdownOpen(o => !o); }}
              >
                {isLoggedIn
                  ? <span className="ffn-avatar">{initial}</span>
                  : <FontAwesomeIcon icon={faUser} className="ffn-account-icon" />}
                <span className="ffn-account-name">{displayName}</span>
                <FontAwesomeIcon icon={faChevronDown} className="ffn-caret" />
              </button>

              <div className={`ffn-menu${isAccountDropdownOpen ? ' is-open' : ''}`}>
                {isLoggedIn ? (
                  <>
                    <div className="ffn-menu-user">
                      <span className="ffn-avatar ffn-avatar--lg">{initial}</span>
                      <span>
                        <small>Signed in as</small>
                        <strong>{user?.pu_name || 'Welcome'}</strong>
                      </span>
                    </div>
                    <button type="button" className="ffn-menu-item" onClick={handleProfileClick}>
                      <FontAwesomeIcon icon={faUser} /> Profile
                    </button>
                    <button type="button" className="ffn-menu-item" onClick={handleWishlistClick}>
                      <FontAwesomeIcon icon={faHeart} /> Wishlist
                    </button>
                    <button type="button" className="ffn-menu-item" onClick={handleOrderHistoryClick}>
                      <FontAwesomeIcon icon={faClipboardList} /> My Orders
                    </button>
                    <div className="ffn-menu-divider" />
                    <button type="button" className="ffn-menu-item ffn-menu-item--danger" onClick={handleLogout}>
                      <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                    </button>
                  </>
                ) : (
                  <>
                    <div className="ffn-menu-intro">
                      <strong>Welcome</strong>
                      <span>Sign in to track orders, save favourites and check out faster.</span>
                    </div>
                    <button
                      type="button"
                      className="ffn-menu-cta"
                      onClick={() => { setShowLogin(true); setIsAccountDropdownOpen(false); }}
                    >
                      Sign in
                    </button>
                    <button
                      type="button"
                      className="ffn-menu-cta ffn-menu-cta--ghost"
                      onClick={() => { setShowRegistration(true); setIsAccountDropdownOpen(false); }}
                    >
                      Create account
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Hamburger */}
          <button type="button" className="ffn-burger" onClick={() => setIsMenuOpen(o => !o)} aria-label="Toggle menu" aria-expanded={isMenuOpen}>
            <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div className={`ffn-overlay${isMenuOpen ? ' is-open' : ''}`} onClick={closeMenu} />

      {/* Mobile drawer */}
      <aside className={`ffn-drawer${isMenuOpen ? ' is-open' : ''}`} aria-hidden={!isMenuOpen}>
        <div className="ffn-drawer-head">
          <BrandLogo tone="dark" />
          <button type="button" className="ffn-drawer-close" onClick={closeMenu} aria-label="Close menu">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="ffn-drawer-body">
          <NavLink to="/" end className={mobileLinkClass} onClick={closeMenu}>Home</NavLink>

          <button type="button" className="ffn-m-link" onClick={() => setIsCategoryOpen(o => !o)} aria-expanded={isCategoryOpen}>
            Collection
            <FontAwesomeIcon icon={faChevronDown} className={`ffn-m-caret${isCategoryOpen ? ' is-open' : ''}`} />
          </button>
          {isCategoryOpen && (
            <div className="ffn-m-cats">
              {categoryData.map((category) => (
                <div key={category.category_id} className="ffn-m-cat">
                  <Link to={`/collection/${category.c_slug}`} className="ffn-m-cat-head" onClick={closeMenu}>
                    {category.c_name}
                  </Link>
                  {category.subcategories.map((sub) => (
                    <Link to={`/products/${sub.sc_slug}`} className="ffn-m-sub" key={sub.sc_slug} onClick={closeMenu}>
                      {sub.sc_name}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}

          <NavLink to="/products" className={mobileLinkClass} onClick={closeMenu}>Products</NavLink>
          <NavLink to="/pages/about-us" className={mobileLinkClass} onClick={closeMenu}>About Us</NavLink>
          <NavLink to="/contact" className={mobileLinkClass} onClick={closeMenu}>Contact</NavLink>

          <div className="ffn-m-label">Quick links</div>
          <Link to="/cart" className="ffn-m-quick" onClick={closeMenu}>
            <FontAwesomeIcon icon={faShoppingCart} /> Cart
            {cartCount > 0 && <span className="ffn-badge ffn-badge--inline">{cartCount}</span>}
          </Link>
          <Link to="/wishlist" className="ffn-m-quick" onClick={closeMenu}>
            <FontAwesomeIcon icon={faHeart} /> Wishlist
          </Link>

          <div className="ffn-m-label">Account</div>
          {isLoggedIn ? (
            <>
              <button type="button" className="ffn-m-quick" onClick={handleProfileClick}>
                <FontAwesomeIcon icon={faUser} /> Profile
              </button>
              <button type="button" className="ffn-m-quick" onClick={handleOrderHistoryClick}>
                <FontAwesomeIcon icon={faClipboardList} /> My Orders
              </button>
              <button type="button" className="ffn-m-quick ffn-m-quick--danger" onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} /> Logout
              </button>
            </>
          ) : (
            <div className="ffn-m-auth">
              <button type="button" className="ffn-menu-cta" onClick={() => { setShowLogin(true); closeMenu(); }}>
                Sign in
              </button>
              <button type="button" className="ffn-menu-cta ffn-menu-cta--ghost" onClick={() => { setShowRegistration(true); closeMenu(); }}>
                Create account
              </button>
            </div>
          )}
        </div>
      </aside>

      <Registration
        show={showRegistration}
        onClose={() => setShowRegistration(false)}
        switchToLogin={() => { setShowRegistration(false); setShowLogin(true); }}
      />
      <Login
        show={showLogin}
        onClose={() => setShowLogin(false)}
        switchToRegistration={() => { setShowLogin(false); setShowRegistration(true); }}
      />
    </div>
  );
};

export default Navbar;